import { useState } from 'react';
import { Horizon, Btn, Mo, Bd, Dp } from './shared';
import { GOLD, DEBIT_RED, MONO } from '../lib/constants';

const API = process.env.REACT_APP_BACKEND_URL;

/* ══════════════════════════════════════════════
   CATEGORY DEFINITIONS
   ══════════════════════════════════════════════ */

const PERSONAL_CATEGORIES = [
  {
    section: 'DISCRETIONARY',
    items: [
      { label: 'Food & Drink',      icon: '🔥', category: 'FOOD_AND_DRINK',    classification: 'personal' },
      { label: 'Dining Out',        icon: '🍽️', category: 'DINING_OUT',        classification: 'personal' },
      { label: 'Entertainment',     icon: '🎬', category: 'ENTERTAINMENT',      classification: 'personal' },
      { label: 'Shopping',          icon: '🛒', category: 'SHOPPING',           classification: 'personal' },
      { label: 'Personal Care',     icon: '💅', category: 'PERSONAL_CARE',      classification: 'personal' },
      { label: 'Travel',            icon: '✈️', category: 'TRAVEL',             classification: 'personal' },
      { label: 'Recreation',        icon: '🏃', category: 'RECREATION',         classification: 'personal' },
      { label: 'Fitness',           icon: '💪', category: 'FITNESS',            classification: 'personal' },
      { label: 'Coffee Shops',      icon: '☕', category: 'COFFEE_SHOPS',       classification: 'personal' },
      { label: 'Pet Care',          icon: '🐾', category: 'PET_CARE',           classification: 'personal' },
      { label: 'Beauty & Salon',    icon: '💇', category: 'BEAUTY_SALON',       classification: 'personal' },
      { label: 'Alcohol & Tobacco', icon: '🍺', category: 'ALCOHOL_TOBACCO',    classification: 'personal' },
    ],
  },
  {
    section: 'ESSENTIAL',
    items: [
      { label: 'Groceries',         icon: '🛒', category: 'GROCERIES',          classification: 'personal' },
      { label: 'Rent & Bills',      icon: '🏠', category: 'RENT_AND_UTILITIES', classification: 'personal' },
      { label: 'Housing',           icon: '🏡', category: 'HOUSING',            classification: 'personal' },
      { label: 'Transportation',    icon: '🚗', category: 'TRANSPORTATION',     classification: 'personal' },
      { label: 'Gas & Fuel',        icon: '⛽', category: 'GAS_FUEL',           classification: 'personal' },
      { label: 'Medical',           icon: '🏥', category: 'MEDICAL',            classification: 'personal' },
      { label: 'Healthcare',        icon: '🩺', category: 'HEALTHCARE',         classification: 'personal' },
      { label: 'Childcare',         icon: '👶', category: 'CHILDCARE',          classification: 'personal' },
      { label: 'Education',         icon: '🎓', category: 'EDUCATION',          classification: 'personal' },
      { label: 'Home Improvement',  icon: '🏠', category: 'HOME_IMPROVEMENT',   classification: 'personal' },
      { label: 'Parking & Tolls',   icon: '🅿️', category: 'PARKING_TOLLS',     classification: 'personal' },
    ],
  },
  {
    section: 'FIXED / RECURRING',
    items: [
      { label: 'Phone Service',     icon: '📱', category: 'PHONE_SERVICE',      classification: 'personal' },
      { label: 'Internet',          icon: '📡', category: 'INTERNET',           classification: 'personal' },
      { label: 'Subscriptions',     icon: '📲', category: 'SUBSCRIPTIONS',      classification: 'subscriptions' },
      { label: 'Storage Unit',      icon: '📦', category: 'STORAGE_UNIT',       classification: 'personal' },
    ],
  },
  {
    section: 'INSURANCE',
    items: [
      { label: 'Personal Insurance', icon: '🛡️', category: 'PERSONAL_INSURANCE', classification: 'insurance' },
      { label: 'Car Insurance',      icon: '🚗', category: 'CAR_INSURANCE',       classification: 'insurance' },
      { label: 'Health Insurance',   icon: '🏥', category: 'HEALTH_INSURANCE',    classification: 'insurance' },
      { label: 'Life Insurance',     icon: '❤️', category: 'LIFE_INSURANCE',      classification: 'insurance' },
    ],
  },
  {
    section: 'DEBT PAYMENTS',
    items: [
      { label: 'Loan / Debt Payment',    icon: '💳', category: 'LOAN_DEBT_PAYMENT',    classification: 'debt_payment' },
      { label: 'Credit Card Payment',    icon: '💸', category: 'CREDIT_CARD_PAYMENT',  classification: 'debt_payment' },
      { label: 'Auto Loan Payment',      icon: '🚙', category: 'AUTO_LOAN_PAYMENT',    classification: 'vehicle_payment' },
      { label: 'Mortgage Payment',       icon: '🏠', category: 'MORTGAGE_PAYMENT',     classification: 'debt_payment' },
      { label: 'Student Loan',           icon: '🎓', category: 'STUDENT_LOAN_PAYMENT', classification: 'debt_payment' },
      { label: 'Personal Loan',          icon: '📄', category: 'PERSONAL_LOAN_PAYMENT',classification: 'debt_payment' },
      { label: 'Medical Debt',           icon: '🏥', category: 'MEDICAL_DEBT_PAYMENT', classification: 'debt_payment' },
      { label: 'Buy Now Pay Later',      icon: '🛒', category: 'BNPL_PAYMENT',         classification: 'debt_payment' },
    ],
  },
  {
    section: 'VEHICLE',
    items: [
      { label: 'Vehicle / Auto Payment', icon: '🚙', category: 'VEHICLE_AUTO', classification: 'vehicle_payment' },
    ],
  },
  {
    section: 'OTHER',
    items: [
      { label: 'Investment',  icon: '📈', category: 'INVESTMENTS', classification: 'investment' },
      { label: 'Other',       icon: '📦', category: 'OTHER',       classification: 'personal' },
    ],
  },
];

const BUSINESS_CATEGORIES = [
  {
    section: 'COST OF GOODS',
    items: [
      { label: 'Cost of Goods / COGS',    icon: '📊', category: 'COST_OF_GOODS',         classification: 'business_cogs' },
      { label: 'Supplies & Equipment',    icon: '📦', category: 'SUPPLIES_EQUIPMENT',     classification: 'business_operations' },
      { label: 'Shipping & Delivery',     icon: '🚚', category: 'SHIPPING_DELIVERY',      classification: 'business_operations' },
    ],
  },
  {
    section: 'OPERATIONS',
    items: [
      { label: 'Business Operations',       icon: '💼', category: 'BUSINESS_OPERATIONS',     classification: 'business_operations' },
      { label: 'Business Rent',             icon: '🏢', category: 'BUSINESS_RENT',           classification: 'business_operations' },
      { label: 'Business Utilities',        icon: '⚡', category: 'BUSINESS_UTILITIES',      classification: 'business_operations' },
      { label: 'Business Software',         icon: '💻', category: 'BUSINESS_SUBSCRIPTIONS',  classification: 'business_operations' },
      { label: 'Advertising & Marketing',   icon: '📢', category: 'ADVERTISING_MARKETING',   classification: 'business_operations' },
      { label: 'Merchant Fees',             icon: '💳', category: 'MERCHANT_FEES',           classification: 'business_operations' },
      { label: 'Professional Services',     icon: '⚖️', category: 'PROFESSIONAL_SERVICES',  classification: 'business_operations' },
      { label: 'POS System Fees',           icon: '🗄️', category: 'POS_SYSTEM_FEES',        classification: 'business_operations' },
      { label: 'Business Phone & Internet', icon: '📱', category: 'BUSINESS_PHONE_INTERNET', classification: 'business_operations' },
      { label: 'Business Travel',           icon: '✈️', category: 'BUSINESS_TRAVEL',        classification: 'business_operations' },
    ],
  },
  {
    section: 'PEOPLE',
    items: [
      { label: 'Payroll & Contractors', icon: '👥', category: 'PAYROLL_CONTRACTORS',  classification: 'payroll_contractors' },
      { label: 'Payroll & Wages',       icon: '💰', category: 'PAYROLL_WAGES',        classification: 'payroll_contractors' },
      { label: 'Contractor Payments',   icon: '🤝', category: 'CONTRACTOR_PAYMENTS', classification: 'payroll_contractors' },
    ],
  },
  {
    section: 'TAXES & INSURANCE',
    items: [
      { label: 'Business Taxes',     icon: '🧾', category: 'BUSINESS_TAXES',     classification: 'business_taxes' },
      { label: 'Business Insurance', icon: '🛡️', category: 'BUSINESS_INSURANCE', classification: 'business_operations' },
    ],
  },
  {
    section: 'VEHICLE',
    items: [
      { label: 'Vehicle / Fleet',        icon: '🚙', category: 'VEHICLE_FLEET', classification: 'vehicle_payment' },
      { label: 'Vehicle / Auto Payment', icon: '🚗', category: 'VEHICLE_AUTO',  classification: 'vehicle_payment' },
    ],
  },
  {
    section: 'DEBT',
    items: [
      { label: 'Loan / Debt Payment', icon: '💳', category: 'LOAN_DEBT_PAYMENT', classification: 'debt_payment' },
      { label: 'MCA Repayment',       icon: '🏦', category: 'MCA_REPAYMENT',     classification: 'debt_payment' },
      { label: 'Business Loan',       icon: '🏛️', category: 'BUSINESS_LOAN',    classification: 'debt_payment' },
      { label: 'SBA Loan',            icon: '📋', category: 'SBA_LOAN',          classification: 'debt_payment' },
      { label: 'Line of Credit',      icon: '📄', category: 'LINE_OF_CREDIT',    classification: 'debt_payment' },
    ],
  },
  {
    section: 'OTHER',
    items: [
      { label: 'Other', icon: '📦', category: 'OTHER', classification: 'business_operations' },
    ],
  },
];

/* ══════════════════════════════════════════════
   CHECKPOINT MODAL COMPONENT
   ══════════════════════════════════════════════ */

export function CheckpointModal({ checkpoints, onComplete, onSkip }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState('type'); // 'type' | 'personal-categories' | 'business-categories' | 'mix-slider' | 'mix-categories'
  const [businessPercent, setBusinessPercent] = useState(50);

  const current = checkpoints[currentIndex];
  const isLastCheckpoint = currentIndex === checkpoints.length - 1;
  const isSpending = current?.type === 'spending';

  const advance = async (classificationData) => {
    const compositeKey = `${current.merchant}__debit`;
    const newAnswers = { ...answers, [compositeKey]: classificationData };

    // Also save under original Plaid merchant name if different (so analyze_spending lookup finds it)
    if (current.original_merchant && current.original_merchant !== current.merchant) {
      const origKey = `${current.original_merchant}__debit`;
      newAnswers[origKey] = classificationData;
    }

    setAnswers(newAnswers);
    setStep('type');
    setBusinessPercent(50);

    if (isLastCheckpoint) {
      await saveClassifications(newAnswers);
      onComplete(newAnswers);
    } else {
      setCurrentIndex(i => i + 1);
    }
  };

  const handleTypeSelect = (type) => {
    if (type === 'personal') setStep('personal-categories');
    else if (type === 'business') setStep('business-categories');
    else if (type === 'mix') setStep('mix-slider');
    else if (type === 'ignore') {
      advance({ type: 'spending', classification: 'ignore', direction: 'debit' });
    }
  };

  const handleCategorySelect = (item, parentType) => {
    if (parentType === 'personal') {
      advance({
        type: 'personal',
        classification: item.classification,
        category_override: item.category,
        direction: 'debit',
      });
    } else if (parentType === 'business') {
      advance({
        type: 'business',
        classification: item.classification,
        category_override: item.category,
        direction: 'debit',
      });
    } else if (parentType === 'mix') {
      advance({
        type: 'spending',
        classification: 'mix',
        business_percent: businessPercent,
        category_override: item.category,
        direction: 'debit',
      });
    }
  };

  const confirmMix = () => {
    setStep('mix-categories');
  };

  const saveClassifications = async (allAnswers) => {
    try {
      await fetch(`${API}/api/insights/merchant-classifications`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classifications: allAnswers }),
      });
    } catch (err) {
      console.error('Failed to save classifications:', err);
    }
  };

  if (!current) return null;

  return (
    <div data-testid="checkpoint-modal" className="dettio-screen">
      <Horizon intensity={0.3} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          {step !== 'type' ? (
            <button
              data-testid="checkpoint-back-btn"
              onClick={() => setStep(step === 'mix-categories' ? 'mix-slider' : 'type')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="2" strokeLinecap="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>BACK</Mo>
            </button>
          ) : (
            <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 2 }}>SMART SCAN</Mo>
          )}
          <button onClick={onSkip} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
            <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>SKIP ALL</Mo>
          </button>
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>CLASSIFYING TRANSACTIONS</Mo>
            <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>{currentIndex + 1} / {checkpoints.length}</Mo>
          </div>
          <div style={{ height: 4, background: 'var(--ghost)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${((currentIndex + 1) / checkpoints.length) * 100}%`, background: GOLD, borderRadius: 2, transition: 'width 0.3s ease' }} />
          </div>
        </div>

        {/* Merchant Info Card — always visible */}
        <div style={{ background: 'var(--surface)', borderRadius: 16, padding: '14px 20px', marginBottom: 16 }}>
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 36 }}>{isSpending ? '🔍' : '💰'}</span>
          </div>
          <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', textAlign: 'center', marginBottom: 8 }}>
            {isSpending ? 'We found transactions from:' : 'We found deposits from:'}
          </Dp>
          <div style={{ background: 'var(--faint)', borderRadius: 12, padding: 14, marginBottom: 0 }}>
            <Dp style={{ fontSize: 15, fontWeight: 700, color: 'var(--light)', textAlign: 'center', marginBottom: 8 }}>
              {current.merchant}
            </Dp>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
              <div style={{ textAlign: 'center' }}>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block' }}>TRANSACTIONS</Mo>
                <Bd style={{ fontSize: 13, color: 'var(--light)' }}>{current.count}</Bd>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block' }}>TOTAL</Mo>
                <Bd style={{ fontSize: 13, color: isSpending ? DEBIT_RED : '#22c55e' }}>
                  {isSpending ? '-' : '+'}${current.total?.toLocaleString()}
                </Bd>
              </div>
            </div>
          </div>
        </div>

        {/* ── STEP 1: Type Selection (Spending) ── */}
        {isSpending && step === 'type' && (
          <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Bd style={{ fontSize: 12, color: 'var(--mute)', textAlign: 'center', marginBottom: 4 }}>What type of expense is this?</Bd>
            <TypeBtn icon="👤" label="Personal Expense"      hint="For you or your household"    color="#22c55e" onClick={() => handleTypeSelect('personal')} testId="type-personal" />
            <TypeBtn icon="💼" label="Business Expense"      hint="For your business"             color="#3b82f6" onClick={() => handleTypeSelect('business')} testId="type-business" />
            <TypeBtn icon="🔀" label="Mix of Both"           hint="Split between personal & biz"  color={GOLD}    onClick={() => handleTypeSelect('mix')}     testId="type-mix" />
            <TypeBtn icon="🚫" label="Ignore"                hint="Skip this merchant"             color="var(--mute)" onClick={() => handleTypeSelect('ignore')}  testId="type-ignore" />
          </div>
        )}

        {/* ── STEP 1: Income Options (unchanged) ── */}
        {!isSpending && step === 'type' && (
          <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Bd style={{ fontSize: 12, color: 'var(--mute)', textAlign: 'center', marginBottom: 4 }}>Is this:</Bd>
            <OptionBtn label="Business Revenue"           icon="🏢" onClick={() => advance({ type: 'income', classification: 'business_revenue', direction: 'credit' })} />
            <OptionBtn label="Personal Income (Salary)"   icon="👤" onClick={() => advance({ type: 'income', classification: 'salary',           direction: 'credit' })} />
            <OptionBtn label="Loan / Cash Advance"        icon="🏦" hint="Not income — borrowed money" onClick={() => advance({ type: 'income', classification: 'loan_advance', direction: 'credit' })} />
            <OptionBtn label="Transfer Between Accounts"  icon="🔄" onClick={() => advance({ type: 'income', classification: 'transfer',         direction: 'credit' })} />
            <OptionBtn label="Investment Return"          icon="📈" onClick={() => advance({ type: 'income', classification: 'investment_return', direction: 'credit' })} />
            <OptionBtn label="Refund"                     icon="↩️" onClick={() => advance({ type: 'income', classification: 'refund',           direction: 'credit' })} />
            <OptionBtn label="Other"                      icon="📋" onClick={() => advance({ type: 'income', classification: 'other',            direction: 'credit' })} />
          </div>
        )}

        {/* ── STEP 2: Personal Categories ── */}
        {step === 'personal-categories' && (
          <CategoryList
            title="PERSONAL EXPENSE"
            groups={PERSONAL_CATEGORIES}
            onSelect={(item) => handleCategorySelect(item, 'personal')}
          />
        )}

        {/* ── STEP 2: Business Categories ── */}
        {step === 'business-categories' && (
          <CategoryList
            title="BUSINESS EXPENSE"
            groups={BUSINESS_CATEGORIES}
            onSelect={(item) => handleCategorySelect(item, 'business')}
          />
        )}

        {/* ── Mix Slider ── */}
        {step === 'mix-slider' && (
          <div className="fade-in" style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 14, textAlign: 'center' }}>
              ROUGHLY WHAT % IS BUSINESS?
            </Mo>
            <div style={{ padding: '0 8px' }}>
              <input
                type="range" min="0" max="100" step="5"
                value={businessPercent}
                onChange={(e) => setBusinessPercent(parseInt(e.target.value))}
                data-testid="business-percent-slider"
                style={{
                  width: '100%', height: 8, appearance: 'none',
                  background: `linear-gradient(to right, #22c55e ${businessPercent}%, ${GOLD} ${businessPercent}%)`,
                  borderRadius: 4, cursor: 'pointer',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, marginBottom: 20 }}>
              <div style={{ textAlign: 'center' }}>
                <Mo style={{ fontSize: 9, color: GOLD, display: 'block' }}>PERSONAL</Mo>
                <Dp style={{ fontSize: 18, fontWeight: 700, color: GOLD }}>{100 - businessPercent}%</Dp>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Mo style={{ fontSize: 9, color: '#22c55e', display: 'block' }}>BUSINESS</Mo>
                <Dp style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>{businessPercent}%</Dp>
              </div>
            </div>
            <Btn gold onClick={confirmMix} data-testid="confirm-mix-btn" style={{ width: '100%' }}>
              CONFIRM — SELECT BUSINESS CATEGORY
            </Btn>
          </div>
        )}

        {/* ── Mix: Business Category Selection ── */}
        {step === 'mix-categories' && (
          <CategoryList
            title={`BUSINESS CATEGORY — ${businessPercent}% BUSINESS`}
            groups={BUSINESS_CATEGORIES}
            onSelect={(item) => handleCategorySelect(item, 'mix')}
          />
        )}

        {/* Tip */}
        {step === 'type' && (
          <div style={{ background: `${GOLD}10`, borderRadius: 10, padding: 12, border: `1px solid ${GOLD}30`, marginTop: 8 }}>
            <Mo style={{ fontSize: 10, color: 'var(--gold)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>💡</span> Your answers are saved for future scans
            </Mo>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Type Selection Button ── */
function TypeBtn({ icon, label, hint, color, onClick, testId }) {
  return (
    <button
      data-testid={`checkpoint-${testId}`}
      onClick={onClick}
      style={{
        width: '100%', padding: '14px 16px',
        background: 'var(--faint)', border: `1px solid var(--ghost)`,
        borderRadius: 12, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 12,
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = color}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--ghost)'}
    >
      <span style={{ fontSize: 22 }}>{icon}</span>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <Bd style={{ fontSize: 13, color: 'var(--light)', display: 'block' }}>{label}</Bd>
        <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{hint}</Mo>
      </div>
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}

/* ── Scrollable Category List ── */
function CategoryList({ title, groups, onSelect }) {
  return (
    <div className="fade-in" style={{ flex: 1, overflowY: 'auto', maxHeight: '55vh' }}>
      <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 2, display: 'block', marginBottom: 12 }}>
        {title}
      </Mo>
      {groups.map((group) => (
        <div key={group.section} style={{ marginBottom: 16 }}>
          <Mo style={{
            fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5,
            display: 'block', marginBottom: 6, paddingLeft: 4,
          }}>
            {group.section}
          </Mo>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {group.items.map((item) => (
              <OptionBtn
                key={item.category}
                label={item.label}
                icon={item.icon}
                onClick={() => onSelect(item)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Option Button ── */
function OptionBtn({ label, icon, onClick, hint }) {
  return (
    <button
      onClick={onClick}
      data-testid={`checkpoint-option-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        width: '100%', padding: '10px 12px',
        background: 'var(--faint)', border: '1px solid var(--ghost)',
        borderRadius: 10, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 10,
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
      onMouseLeave={e => e.currentTarget.style.background = 'var(--faint)'}
    >
      <span style={{ fontSize: 15 }}>{icon}</span>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <Bd style={{ fontSize: 12, color: 'var(--light)', display: 'block' }}>{label}</Bd>
        {hint && <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{hint}</Mo>}
      </div>
      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="2">
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}
